import type { PersistedResult, PersistedSession, ScanResultEntry } from '../types';
import { WriteQueue } from './writeQueue';

// localStorage write taxonomy for this feature:
//   - persist(): the per-sweep snapshot blob — goes through this.queue
//     (WriteQueue) so multi-QR fan-out can't race a read-modify-write.
//     Keyed by SESSION_PREFIX + sessionId.
//   - SESSION_ID_KEY + clearPersisted() removals: bare localStorage calls,
//     not queued. Safe because they write/remove DISJOINT keys from the
//     queued snapshot writes.
//   - SCAN_MODEL_KEY (the model-selection persistence in ContinuousScanPage):
//     also bare; disjoint key. Same justification.
const SESSION_ID_KEY = 'par-excellence-proto_scan_sessionId';
const SESSION_PREFIX = 'par-excellence-proto_scan_';
const RESUME_WINDOW_MS = 30 * 60 * 1000;

export interface SessionSnapshot {
  sessionId: string;
  dedupe: Set<string>;
  results: ScanResultEntry[];
}

/**
 * Per-tab continuous-scan session: dedupe Set + result list + serialized
 * localStorage persistence. The dedupe Set and results array are
 * authoritative in-memory state; localStorage is a write-only mirror used
 * only on page-load for "Resume previous sweep" prompts.
 *
 * Persisted entries drop the `snapshotBlob` (not serializable) — resumed
 * results render without thumbnails, by design.
 */
export class SessionStore {
  private readonly queue = new WriteQueue();
  readonly sessionId: string;
  readonly dedupe: Set<string>;
  readonly results: ScanResultEntry[];

  private constructor(sessionId: string, dedupe: Set<string>, results: ScanResultEntry[]) {
    this.sessionId = sessionId;
    this.dedupe = dedupe;
    this.results = results;
  }

  static create(): SessionStore {
    const sessionId = crypto.randomUUID();
    try {
      localStorage.setItem(SESSION_ID_KEY, sessionId);
    } catch {
      // localStorage unavailable (private mode, etc.) — fine, we just lose persistence.
    }
    return new SessionStore(sessionId, new Set(), []);
  }

  static resume(snapshot: SessionSnapshot): SessionStore {
    try {
      localStorage.setItem(SESSION_ID_KEY, snapshot.sessionId);
    } catch {
      /* see create() */
    }
    return new SessionStore(snapshot.sessionId, snapshot.dedupe, snapshot.results);
  }

  hasDecoded(qrValue: string): boolean {
    return this.dedupe.has(qrValue);
  }

  markDecoded(qrValue: string): void {
    this.dedupe.add(qrValue);
  }

  forgetDecoded(qrValue: string): void {
    this.dedupe.delete(qrValue);
  }

  addResult(entry: ScanResultEntry): void {
    this.results.unshift(entry);
  }

  updateResult(idempotencyKey: string, patch: Partial<ScanResultEntry>): void {
    const idx = this.results.findIndex((r) => r.idempotencyKey === idempotencyKey);
    if (idx === -1) return;
    this.results[idx] = { ...this.results[idx], ...patch };
  }

  removeResult(idempotencyKey: string): void {
    const idx = this.results.findIndex((r) => r.idempotencyKey === idempotencyKey);
    if (idx !== -1) this.results.splice(idx, 1);
  }

  /**
   * Serializes a snapshot of current state to localStorage. Calls are
   * queued so two near-simultaneous responses can't race a read-modify-
   * write. Drops snapshotBlob — blobs aren't structured-cloneable into
   * localStorage and would bloat the quota.
   */
  persist(): Promise<void> {
    return this.queue.enqueue(() => {
      const persisted: PersistedSession = {
        sessionId: this.sessionId,
        lastUpdated: Date.now(),
        dedupe: [...this.dedupe],
        results: this.results.map(toPersisted),
      };
      try {
        localStorage.setItem(keyFor(this.sessionId), JSON.stringify(persisted));
      } catch {
        // Quota exceeded or storage unavailable — non-fatal for current sweep.
      }
    });
  }

  clearPersisted(): void {
    try {
      localStorage.removeItem(keyFor(this.sessionId));
      localStorage.removeItem(SESSION_ID_KEY);
    } catch {
      /* ignore */
    }
  }
}

function toPersisted(entry: ScanResultEntry): PersistedResult {
  return {
    idempotencyKey: entry.idempotencyKey,
    qrValue: entry.qrValue,
    capturedAt: entry.capturedAt,
    status: entry.status,
    errorMessage: entry.errorMessage,
    response: entry.response,
    operatorTruth: entry.operatorTruth,
  };
}

function keyFor(sessionId: string): string {
  return `${SESSION_PREFIX}${sessionId}`;
}

/**
 * Best-effort lookup of a resumable session. Returns null if none exists,
 * is older than RESUME_WINDOW_MS, or is malformed.
 *
 * Note: blobs are not recovered — resumed results carry the persisted
 * response metadata but render without thumbnails.
 */
export function readResumableSession(): SessionSnapshot | null {
  try {
    const id = localStorage.getItem(SESSION_ID_KEY);
    if (!id) return null;
    const raw = localStorage.getItem(keyFor(id));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSession;
    if (!parsed || typeof parsed !== 'object' || parsed.sessionId !== id) return null;
    if (Date.now() - parsed.lastUpdated > RESUME_WINDOW_MS) return null;

    const dedupe = new Set(Array.isArray(parsed.dedupe) ? parsed.dedupe : []);
    const results: ScanResultEntry[] = (parsed.results ?? []).map((r) => ({
      idempotencyKey: r.idempotencyKey,
      qrValue: r.qrValue,
      // Persisted entries don't carry the original Blob — fall back to an
      // empty placeholder. The UI handles `size === 0` by skipping the
      // thumbnail render.
      snapshotBlob: new Blob([], { type: 'image/jpeg' }),
      capturedAt: r.capturedAt,
      status: r.status,
      errorMessage: r.errorMessage,
      response: r.response,
      operatorTruth: r.operatorTruth,
    }));
    return { sessionId: id, dedupe, results };
  } catch {
    return null;
  }
}
